import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, TextField, Button, Table, TableBody, 
    TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, Select, MenuItem, FormControl, InputLabel, Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

const Budget = ({ data = [], categories = [], updateData, updateCategories, exchangeRates = {} }) => {
    const { t } = useTranslation();
    const currentLanguage = i18n.language;
    const [amount, setAmount] = useState('');
    const [currency, setCurrency] = useState('RON');
    const [category, setCategory] = useState('');
    const [subCategory, setSubCategory] = useState('');
    const [description, setDescription] = useState('');
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newSubCategoryName, setNewSubCategoryName] = useState('');
    const [showAddCategory, setShowAddCategory] = useState(false);

    const defaultCategories = [
        { id: 'food', label: t('FamilyManager.Budget.Food', 'Étel'), subCategories: [] },
        { id: 'clothing', label: t('FamilyManager.Budget.Clothing', 'Ruházat'), subCategories: [] },
        { id: 'utilities', label: t('FamilyManager.Budget.Utilities', 'Rezsi'), subCategories: [] },
        { id: 'entertainment', label: t('FamilyManager.Budget.Entertainment', 'Szórakozás'), subCategories: [] },
        { id: 'education', label: t('FamilyManager.Budget.Education', 'Tanítási költség'), subCategories: [] },
        { id: 'car', label: t('FamilyManager.Budget.Car', 'Autó'), subCategories: [] },
        { id: 'other', label: t('FamilyManager.Budget.Other', 'Egyéb'), subCategories: [] },
    ];

    const allCategories = categories.length > 0 ? categories : defaultCategories;

    useEffect(() => {
        if (allCategories.length > 0 && !category) {
            setCategory(allCategories[0].id);
        }
    }, [allCategories, category]);

    const handleAdd = () => {
        if (!amount || isNaN(amount)) return;
        
        const newItem = {
            id: Date.now(),
            amount: parseFloat(amount),
            currency,
            category,
            subCategory,
            description,
            date: new Date().toISOString()
        };
        
        console.log("Adding budget item:", newItem);
        updateData([...data, newItem]);
        setAmount('');
        setDescription('');
    };

    const handleAddCategory = () => {
        if (!newCategoryName) return;
        const newCat = {
            id: `cat_${Date.now()}`,
            label: newCategoryName,
            subCategories: []
        };
        updateCategories([...allCategories, newCat]);
        setNewCategoryName('');
        setCategory(newCat.id);
    };

    const handleAddSubCategory = () => {
        if (!newSubCategoryName || !category) return;
        const updatedCategories = allCategories.map(cat => {
            if (cat.id === category) {
                return {
                    ...cat,
                    subCategories: [...(cat.subCategories || []), { id: `sub_${Date.now()}`, label: newSubCategoryName }]
                };
            }
            return cat;
        });
        updateCategories(updatedCategories);
        setNewSubCategoryName('');
    };

    const handleDelete = (id) => {
        updateData(data.filter(item => item.id !== id));
    };

    const calculateTotalInRON = () => {
        if (!Array.isArray(data)) return 0;
        return data.reduce((sum, item) => {
            const val = parseFloat(item.amount);
            if (isNaN(val)) return sum;
            
            const itemCurrency = item.currency || 'RON';
            if (itemCurrency === 'RON') return sum + val;
            
            const rate = exchangeRates[itemCurrency.toLowerCase()];
            if (rate && !isNaN(rate)) {
                return sum + (val * parseFloat(rate));
            }
            
            return sum + val; // Fallback ha nincs árfolyam
        }, 0);
    };

    const totalRON = calculateTotalInRON();

    const selectedCategory = allCategories.find(c => c.id === category);

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>{t('FamilyManager.Budget.MainTitle', 'Családi költségvetés')}</Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        label={t('FamilyManager.Budget.Amount', 'Összeg')}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        type="number"
                        size="small"
                        sx={{ width: 100 }}
                    />
                    <FormControl size="small" sx={{ width: 85 }}>
                        <Select
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                        >
                            <MenuItem value="RON">RON</MenuItem>
                            <MenuItem value="HUF">HUF</MenuItem>
                            <MenuItem value="EUR">EUR</MenuItem>
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="GBP">GBP</MenuItem>
                            <MenuItem value="CHF">CHF</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>{t('FamilyManager.Budget.Category', 'Kategória')}</InputLabel>
                    <Select
                        value={category}
                        label={t('FamilyManager.Budget.Category', 'Kategória')}
                        onChange={(e) => { setCategory(e.target.value); setSubCategory(''); }}
                    >
                        {allCategories.map(cat => (
                            <MenuItem key={cat.id} value={cat.id}>{cat.label}</MenuItem>
                        ))}
                        <Divider />
                        <MenuItem onClick={() => setShowAddCategory(true)} sx={{ color: 'primary.main' }}>
                            <AddIcon fontSize="small" sx={{ mr: 1 }} /> {t('FamilyManager.Budget.NewCategory', 'Új kategória')}
                        </MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>{t('FamilyManager.Budget.SubCategory', 'Alkategória')}</InputLabel>
                    <Select
                        value={subCategory}
                        label={t('FamilyManager.Budget.SubCategory', 'Alkategória')}
                        onChange={(e) => setSubCategory(e.target.value)}
                        disabled={!category}
                    >
                        <MenuItem value=""><em>{t('Common.None', 'Nincs')}</em></MenuItem>
                        {selectedCategory?.subCategories?.map(sub => (
                            <MenuItem key={sub.id} value={sub.id}>{sub.label}</MenuItem>
                        ))}
                        <Divider />
                        <MenuItem onClick={() => setShowAddCategory(true)} sx={{ color: 'primary.main' }}>
                            <AddIcon fontSize="small" sx={{ mr: 1 }} /> {t('FamilyManager.Budget.NewSubCategory', 'Új alkategória')}
                        </MenuItem>
                    </Select>
                </FormControl>

                <TextField
                    label={t('FamilyManager.Budget.Description', 'Megjegyzés')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    size="small"
                />
                <Button 
                    variant="contained" 
                    onClick={handleAdd}
                    startIcon={<AddIcon />}
                >
                    {t('Common.Add', 'Hozzáadás')}
                </Button>
            </Box>

            {showAddCategory && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom>{t('FamilyManager.Budget.ManageCategories', 'Kategóriák kezelése')}</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField 
                            label={t('FamilyManager.Budget.NewCategoryName', 'Új kategória név')} 
                            value={newCategoryName} 
                            onChange={(e) => setNewCategoryName(e.target.value)} 
                            size="small" 
                        />
                        <Button variant="outlined" onClick={handleAddCategory} size="small">{t('Common.Add', 'Hozzáadás')}</Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField 
                            label={t('FamilyManager.Budget.NewSubCategoryName', 'Új alkategória név')} 
                            value={newSubCategoryName} 
                            onChange={(e) => setNewSubCategoryName(e.target.value)} 
                            size="small" 
                            disabled={!category}
                            helperText={t('FamilyManager.Budget.SubCategoryHelper', 'A választott kategóriához')}
                        />
                        <Button variant="outlined" onClick={handleAddSubCategory} size="small" disabled={!category}>{t('Common.Add', 'Hozzáadás')}</Button>
                    </Box>
                    <Button sx={{ mt: 1 }} onClick={() => setShowAddCategory(false)}>{t('Common.Close', 'Bezárás')}</Button>
                </Paper>
            )}

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell>{t('FamilyManager.Budget.Category', 'Kategória')}</TableCell>
                            <TableCell>{t('FamilyManager.Budget.Description', 'Leírás')}</TableCell>
                            <TableCell align="right">{t('FamilyManager.Budget.Amount', 'Összeg')}</TableCell>
                            <TableCell align="right">{t('Common.Actions', 'Műveletek')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(data) && data.map((item) => {
                            const cat = allCategories.find(c => c.id === item.category);
                            const subCat = cat?.subCategories?.find(s => s.id === item.subCategory);
                            return (
                                <TableRow key={item.id}>
                                    <TableCell>
                                        {cat?.label || item.category}
                                        {subCat && ` - ${subCat.label}`}
                                    </TableCell>
                                    <TableCell>{item.description}</TableCell>
                                    <TableCell align="right">
                                        {item.amount.toLocaleString(currentLanguage, { minimumFractionDigits: 2 })} {item.currency || 'RON'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleDelete(item.id)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {(!Array.isArray(data) || data.length === 0) && (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                    {t('FamilyManager.Budget.Empty', 'Nincs még rögzített tétel.')}
                                </TableCell>
                            </TableRow>
                        )}
                        <TableRow>
                            <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>{t('FamilyManager.Budget.Total', 'Összesen')} (RON)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {totalRON.toLocaleString(currentLanguage, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RON
                            </TableCell>
                            <TableCell />
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default Budget;
